/**
 * Words for the new library looks (Gallery, Strips), the quick look at one video, and the
 * "find what was said" search.
 */
import { LibraryLook } from "./libraryLook";

export const LOOK_NAMES: Record<LibraryLook, { name: string; icon: string; title: string }> = {
  gallery: { name: "Gallery", icon: "▦", title: "Big pictures of your videos" },
  strips: { name: "Strips", icon: "☰", title: "Each video with its best clips beside it" },
  classic: { name: "Classic", icon: "⊞", title: "Today's table" },
};

export const LOOK_TEXT = {
  label: "Look",
  makeDefault: "Make this the look for everyone",
  everyoneSees: (name: string) => `Everyone sees: ${name}`,
  confirmDefault: (name: string) => `Make "${name}" the library look for everyone? People who already picked a look keep theirs.`,
  savedDefault: (name: string) => `Done. "${name}" is now the look for everyone.`,
};

export const SHELL = {
  uploads: "Uploads",
  exports: "Exports",
  more: "More",
  searchSaid: "Search what was said",
  meetings: "Meetings",
  calendar: "Calendar",
  autoPosting: "Auto-posting",
  artistic: "Artistic edit",
  home: "Home",
  library: "My Library",
  learn: "Learning Center",
  pricing: "Pricing",
  upload: "Upload",
  title: "My Library",
};

export const TOOLBAR = {
  findVideo: "Find a video by name…",
  sort: "Sort",
  size: "Size",
  combine: "Combine videos",
  combining: "Tick the videos you want, then press “Open as one sheet”.",
  stopCombining: "Stop combining",
  more: "More",
  combineTitle: "Tick several videos and open them as ONE sheet: filter, search, pick and export across all of them",
  openCombined: "Open as one sheet →",
  picked: (n: number) => `${n} video${n === 1 ? "" : "s"} picked`,
  noMatches: "Nothing matches that name.",
};

export const SORT_FILTER = {
  sort: "Sort",
  from: "From",
  sources: [
    { key: "", label: "Anywhere" },
    { key: "web", label: "Website" },
    { key: "premiere", label: "Premiere" },
    { key: "mobile", label: "Phone app" },
    { key: "url", label: "Pasted link" },
  ],
  noneFrom: "No videos from there yet.",
};

export const CARD = {
  clips: (n: number) => `${n.toLocaleString()} clip${n === 1 ? "" : "s"}`,
  making: "Making clips…",
  makingNote: "We'll email you when they're ready",
  openClips: "Open clips",
  top20: "Top 20",
  top20Title: "The 20 most postable clips, ready to export and post",
  quickLookTitle: "Watch the video, play its clips, pick and download them",
  inSheet: "In the sheet",
  addToSheet: "Add to the sheet",
  bestMoments: "best moments",
  more: (n: number) => `+${n.toLocaleString()} more`,
  noClipsYet: "No clips to show yet.",
  quickLook: "Quick look",
  fullSheet: "Full sheet",
  fullSheetTitle: "Every clip with its transcript, in a spreadsheet you can filter by speaker, emotion, words and more",
};

export const QUICK = {
  back: "← My Library",
  openSheet: "Open the full sheet",
  openSheetNote: "Every clip with its transcript, like a spreadsheet. Sort any column, filter by time, speaker, emotion or words, pick clips in any order, add captions, then download or share.",
  exporter: "More export options",
  exporterTitle: "Open these clips in the full sheet's exporter: captions, shape, Super Cut and more",
  top20: "Top 20 clips",
  playing: "Playing",
  raw: "The whole video",
  clipsTitle: "Every sentence is a clip",
  legendPlay: "Click a clip to play it",
  legendPick: "Cherry-pick it",
  legendDownload: "Download it",
  seeAll: (n: number) => `See all ${n.toLocaleString()} clips in the full sheet →`,
  loading: "Loading clips…",
  error: "Couldn't load the clips.",
  playHint: "Click a clip to play it. The clips follow the video as it plays.",
  basket: "Your cherry picks",
  basketEmpty: "Cherry-pick clips with +. They line up here: play them in a row, stitch them into one video, or download each.",
  basketHint: "Drag to change the order · click to play · × to take out",
  moveLeft: "Move earlier",
  moveRight: "Move later",
  all: "Every sentence",
  cards: "Cards",
  list: "List",
  allTitle: "The whole video, one clip per sentence, following the player like a transcript",
  find: "Search what's said…",
  findClear: "Clear the search",
  noMatch: (q: string) => `No clip says “${q}”.`,
  superCut: (q: string, n: number) => `✂ Super Cut: every “${q}” in one video (${n})`,
  superCutTitle: "Cut out just these words from every clip that says them, back to back, into one video. It also lands in Exports.",
  textNormal: "Aa",
  textBig: "Aa Big",
  textNormalTitle: "Normal text under the clips",
  textBigTitle: "Bigger, bolder text under the clips",
  pick: "Cherry-pick this clip",
  unpick: "Un-pick",
  picked: (n: number) => `${n} clip${n === 1 ? "" : "s"} picked`,
  playPicks: "Play",
  stitch: "Stitch & download",
  stitched: (n: number) => `${n} clips stitched`,
  each: "Download each",
  eachTitle: "Make each picked clip its own video, then download them one by one",
  oneList: "Pick from one list to stitch",
  clear: "Clear the picks",
  making: "Making… (about a minute)",
  makingTitle: "Cutting the video with captions. It also goes to Exports.",
  download: "⬇ Download",
  downloadTitle: "Download the video",
  makeTitle: "Make this clip a video with captions, ready to share (it also goes to Exports)",
  failed: "That didn't work. Try again.",
  tryAgain: "Try again",
};

export const SAID = {
  title: "Search what was said",
  sub: "Every word in every video. Results are clips you can play.",
  placeholder: "Type a word or a line…",
  searching: "Searching…",
  none: (q: string) => `Nobody said "${q}" in your videos.`,
  count: (n: number, v: number) => `${n.toLocaleString()} clip${n === 1 ? "" : "s"} in ${v} video${v === 1 ? "" : "s"}`,
  capped: "Showing the first 200.",
  openVideo: "Quick look",
  error: "Search didn't work. Try again.",
};

export const MORE_WAYS = {
  title: "More ways to play",
  ai: "Ask your AI to edit",
  aiNote: "“Make a 30-second reel of the best moments.”",
  aiGo: "Choose",
  close: "Close",
  phone: "Get the phone app",
  phoneNote: "Make clips from the videos on your phone.",
  phoneGo: "Get the app",
  meetings: "Clips from your Google Meet calls",
  meetingsNote: "Record a Meet and its clips come to your email, ready to share.",
  meetingsGo: "Set up",
  appStoreUrl: "https://apps.apple.com/us/app/cherryclipz/id6810239977",
  playUrl: "https://play.google.com/store/apps/details?id=com.cherryclipz.app",
  premiere: "Open in Premiere Pro",
  premiereNote: "These clips inside Premiere, with the CherryClipz panel.",
  premiereGo: "Learn how",
  share: "Share link",
  shareNote: "Open your clips anywhere: on your phone, another computer, or send them to anyone. No account needed.",
  shareGo: "Copy link",
};

export const AI_WAYS = {
  app: "AI link",
  best: "Best",
  appNote: "Link your favorite AI (ChatGPT, Claude or Gemini) and watch it cherry-pick clips for you, right on its own site. Uses an MCP connector.",
  appGo: "How to link it",
  fast: "Black Cherry",
  fastBadge: "Fast",
  fastNote: "Paste your AI key into Black Cherry, then just ask it for the edit you want.",
  setUp: "Set up",
  ready: "Ready",
  step1: "Get a key at",
  step2: "then paste it:",
  save: "Save",
  saved: (name: string) => `Your ${name} key is saved in this browser, for every video`,
  openBlackCherry: "Open the sheet with Black Cherry",
  privacy: "The key stays in this browser. The AI company bills your own account.",
};

export const EXPORTS_PAPER = {
  category: { all: "All", auto: "Made by Auto-Clip", manual: "Made by you" } as Record<"all" | "auto" | "manual", string>,
  categoryTitle: "Auto-Clip makes some exports for you in one click; the rest are the ones you exported",
  find: "Find an export by title…",
  cards: "Cards",
  list: "List",
  size: "Card size",
  copyHint: "Click to copy the text",
  copied: "Copied ✓",
  basket: "Stitch these together",
  basketNote: (n: number, max: number) => `${n} picked · up to ${max} · drag to change the order`,
  clear: "Clear",
  remove: "Take out",
  titlePlaceholder: "Name the stitched video…",
  stitch: "Stitch & export",
  stitchingNow: "Stitching…",
  downloadAll: (n: number) => `Download all ${n}`,
  downloading: "Downloading…",
  stitching: (title: string) => `“${title}” is being stitched. It shows up here when it's ready.`,
  pick: "Add to the stitch",
  unpick: "Take out of the stitch",
  play: "Play",
  stitched: "Stitched",
  making: "Making… it shows up here when it's ready",
  copyText: "Copy text",
  download: "Download",
};

export const FIRST_HINT = {
  text: "Quick look: watch the video and grab clips. Full sheet: every clip with every tool. Top 20: the clips most ready to post.",
  close: "Got it",
};
